<template>
  <div class="build" v-if="filteredAppList">
    <div
      style="width: fit-content;"
      class="d-flex align-center link-default mb-4"
      @click="$router.push('/dashboard')"
    >
      <apio-icon
        class="mr-1"
        size="16"
        lightModeColour="cyan"
        name="chevron-left"
      />Back
    </div>
    <HeaderPanel
      class="build__header-panel"
      header-icon="form-app-apioshade3"
      header-text="Build"
    >
      <div class="build__header-panel-filter d-flex align-center">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-text-field
              v-on="on"
              v-model="q"
              placeholder="Start searching..."
              dense
              outlined
              hide-details="auto"
              class="m-0 build__filter"
              @input="getApps()"
            ><template v-slot:append><apio-icon name="ui-search" /></template>
            </v-text-field>
          </template>
          <span
            >Filter for any content or program code in interface declarations
            and environments</span
          >
        </v-tooltip>
      </div>

      <div
        slot="activator"
        v-if="$mq === 'small' || $mq === 'medium' || $mq === 'large'"
        ><apio-button
          button-size="small"
          button-text-size="tiny"
          class="users__add-button"
          @click="toggleModal({ mode: 'new' })"
          >NEW APP
          <apio-icon
            class="ml-1"
            name="form-app"
            size="24"
            darkModeColour="white"
            lightModeColour="white" /></apio-button
          ></div
      >

      <div slot="header-cta" v-else
        ><apio-button
          class="users__add-button"
          @click="toggleModal({ mode: 'new' })"
          >New app
          <apio-icon
            class="ml-1"
            name="form-app"
            size="32"
            darkModeColour="white"
            lightModeColour="white" /></apio-button
      ></div>
      <div slot="header-right" v-if="loaded">
        <div class="build__app-select-wrapper">
          <span class="build__app-select--label">View apps</span>
          <vue-select
            append-to-body :calculate-position="withPopper"
            v-if="apps"
            v-model="selectedApp"
            id="app_select"
            class="build__app-select"
            :class="{ 'vs__actions--dark vs__dropdown-toggle--dark' : isDarkMode }"
            :options="apps"
            placeholder="All"
            ref="select"
            label="real_name"
            :reduce="(item) => item.appID"
            @input="selectApp"
          >
          </vue-select>
        </div>
      </div>
    </HeaderPanel>

    <div v-if="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm" class="build__app-select-wrapper--mobile">
      <span class="build__app-select--label">View apps</span>
      <vue-select
        append-to-body :calculate-position="withPopper"
        v-if="apps"
        v-model="selectedApp"
        id="app_select"
        class="build__app-select"
        :options="apps"
        placeholder="All"
        ref="select"
        label="real_name"
        :reduce="(item) => item.appID"
        @input="selectApp"
      >
      </vue-select>
    </div>

    <div
      v-if="filteredAppList && filteredAppList.length && loaded">
      <BuildApp
        v-for="(app, index) in filteredAppList"
        :key="index"
        :app="app"
        :appCount="filteredAppList.length"
        :clientHash="clientHash === '*' ? app.clientHash : clientHash"
        @toggleModal="toggleModal($event)"
        @deleteApp="deleteAppModal($event)"
        @renameApp="renameApp($event)"
      />
    </div>

    <div v-else-if="!loaded" class="build__loader">
      <ContentLoader viewBox="0 0 100 100">
          <rect x="0" y="0" rx="3" ry="3" width="250" height="10" />
          <rect x="20" y="20" rx="3" ry="3" width="220" height="10" />
          <rect x="20" y="40" rx="3" ry="3" width="170" height="10" />
          <rect x="0" y="60" rx="3" ry="3" width="250" height="10" />
          <rect x="20" y="80" rx="3" ry="3" width="200" height="10" />
          <rect x="20" y="100" rx="3" ry="3" width="80" height="10" />
      </ContentLoader>
    </div>
    <div v-else-if="loaded && filteredAppList.length === 0">
      <apio-banner
        :size="bannerSize"
        class="mt-8 mb-8"
        :icon="'apps'"
        :title="'You haven\'t created any apps yet'"
        :type="'add_app'"
        :title-text-size="'h1'"
        :content-text="`Add at least one app to your organisation to get started.`"
      >
        <apio-button
          button-type="secondary"
          slot="button"
          style="text-transform: none"
          @click="toggleModal({ mode: 'new' })"
          >New app</apio-button
        >
      </apio-banner>
    </div>

    <!-- Modal for creating/copy app -->
    <v-dialog v-model="modalStatus" width="600">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">
            {{
              modalType === "copy"
                ? "Copy app"
                : modalType === "rename"
                ? "Rename app"
                : "Create new app"
            }}
          </div>
          <v-spacer/>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="modalStatus = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text>
          <div class="build__modal">
            <validation-observer
              tag="form"
              ref="modal-form"
              class="build__modal-form"
              @submit.prevent="validateForm()"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                name="app name"
                tag="div"
                class="build__modal-form-group"
                v-if="modalType === 'new'"
              >
                <v-text-field
                  id="appName"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Enter your app name"
                  v-model="newAppName"
                  ref="inputModal"
                />
                <transition name="fade">
                  <span class="build__modal-form-error" v-if="errors[0]">
                    {{ errors[0] }}
                  </span>
                </transition>
              </validation-provider>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                name="app name"
                tag="div"
                class="build__modal-form-group"
                v-else-if="modalType === 'copy'"
              >
                <label for="newAppName">New app name</label>
                <v-text-field
                  id="newAppName"
                  placeholder="Enter your new app name"
                  dense
                  outlined
                  hide-details="auto"
                  v-model="copyAppName"
                  ref="inputModal"
                />
                <transition name="fade">
                  <span class="build__modal-form-error" v-if="errors[0]">
                    {{ errors[0] }}
                  </span>
                </transition>
              </validation-provider>
              <p v-else>Error</p>
            </validation-observer>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer />
          <div class="build__modal-actions" slot="footer">
            <apio-button
              button-type="secondary"
              button-size="medium"
              class="mr-2"
              @click="toggleModal()"
            >
              Cancel
            </apio-button>
            <apio-button
              button-size="medium"
              v-if="modalType === 'new'"
              @click="validateForm()"
            >
              Create
            </apio-button>
            <apio-button
              button-size="medium"
              v-if="modalType === 'copy'"
              @click="validateForm()"
            >
              Copy
            </apio-button>
            <apio-button
              button-size="medium"
              @click="validateForm()"
              v-if="modalType === 'rename'"
            >
              Rename
            </apio-button>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Modal for deleting app -->
    <v-dialog width="600" v-model="deleteModalStatus">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">Delete app</div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="deleteModalStatus = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text class="pb-0">
          <div>
            <span>
              Are you sure that you want to delete
              <span class="build__modal-bold">
                {{ appToDelete.name }}
              </span>
              app?
            </span>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer />
          <apio-button
            button-size="medium"
            button-type="secondary"
            class="mr-2"
            @click="deleteAppModal({})"
          >
            Cancel
          </apio-button>
          <apio-button button-size="medium" @click="deleteApp()">
            Delete
          </apio-button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { createPopper } from "@popperjs/core";
import debounce from "lodash.debounce";

export default {
  name: "BuildApps",
  components: {
    ApioBanner: () => import("@/components/Banner/Banner.vue"),
    HeaderPanel: () => import("@/components/HeaderPanel/HeaderPanel.vue"),
    BuildApp: () => import("@/components/BuildApp/BuildApp.vue"),
  },
  created() {
    this.clientHash = this.$route.query.c;
    this.$store.dispatch("getApps", {
      c: this.$route.query.c,
      q: "",
    });
    this.loaded = true;
  },
  data: () => ({
    modalStatus: false,
    deleteModalStatus: false,
    appToDelete: {},
    modalType: "",
    copyAppName: "",
    newAppName: "",
    currentApp: "",
    copiedApp: {},
    clientHash: "",
    q: "",
    hasSearched: false,
    selectedApp: "",
    placement: "bottom",
    loaded: false,
  }),
  watch: {
    $route() {
      this.q = "";
      this.clientHash = this.$route.query.c;

      this.$store.dispatch("getApps", {
        c: this.$route.query.c,
        q: "",
      });
    },
  },
  computed: {
    apps() {
      return this.$store.state.apps.apps;
    },
    isDarkMode() {
      return this.$store.state.mode.darkMode
    },
    bannerSize() {
      return (this.$mq === 'small' || this.$mq === 'medium' || this.$mq === 'large') ? 'tall' : 'wide';
    },
    filteredAppList() {
      const selectedApp = this.$route.query.a;
      if(selectedApp !== undefined) {
        return this.$store.state.apps.apps.filter((item) => {
          return item.appID === selectedApp;
        })
      }
      else {
        return this.$store.state.apps.apps;
      }
    }
  },
  mounted() {
    const filteredAppHash = this.getFilteredAppForClient();
    if(filteredAppHash) {
      this.selectedApp = filteredAppHash;
      this.selectApp(filteredAppHash);
    }
    this.$root.$on('getApps', () => {
      this.getApps();
    })
  },
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      await vm.$store.dispatch("getApps", {
        c: vm.$route.query.c,
        q: "",
      });
    });
  },
  methods: {
    getFilteredAppForClient() {
      return this.$store.getters.getFilteredAppForClient(this.$route.query.c);
    },
    selectApp(val) {
      this.$store.dispatch('setFilteredAppForClient', { clientHash: this.$route.query.c, appHash: val })

      if(val !== null) {
        this.$router.push(`/build?c=${this.$route.query.c}&a=${val}`)
      }
      else {
        this.$router.push(`/build?c=${this.$route.query.c}`)
      }
    },
    withPopper(dropdownList, component, { width }) {
      const vm = this;
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = width;

      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, -1],
            },
          },
          {
            name: "toggleClass",
            enabled: true,
            phase: "write",
            fn() {
              component.$el.style.backgroundColor = vm.isDarkMode ? '#2a324f' : '#ffffff';
              component.$el.style.color = vm.isDarkMode ? '#ffffff' : '#000000';
              dropdownList.style.backgroundColor = vm.isDarkMode ? '#2a324f' : '#ffffff';
              dropdownList.style.color = vm.isDarkMode ? '#ffffff' : '#ffffff';
              dropdownList.style.fontFamily = 'Gilroy';
            },
          },
        ],
      });

      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => popper.destroy();
    },
    getApps: debounce(function () {
      this.$store.dispatch("getApps", {
        c: this.$route.query.c,
        q: this.q,
      });
    }, 600),
    async createNewApp() {
      const app_id = await this.$store.dispatch("createNewApp", {
        appName: this.newAppName,
        clientHash: this.clientHash,
      });
      this.newAppName = '';
      this.toggleModal();
      if(app_id && this.$route.query.a) { //check for filtered app here
        this.selectApp(null);
        this.selectedApp = null;
      }
    },
    deleteApp() {
      this.$store.dispatch("deleteApp", {
        appID: this.appToDelete.appID,
        clientHash: this.clientHash,
      });
      this.deleteModalStatus = false;
      if(this.selectedApp === this.appToDelete.appID) {
        this.selectedApp = null;
        this.selectApp(null)
      }
    },
    deleteAppModal(payload) {
      this.deleteModalStatus = !this.deleteModalStatus;
      this.appToDelete = payload;
    },
    toggleModal(payload) {
      this.modalStatus = !this.modalStatus;

      if (payload) {
        this.modalType = payload.mode;
        this.copiedApp = this.modalType === "copy" ? payload.app : {};
        this.currentApp = this.modalType === "rename" ? payload.app : {};
      }
    },
    copyApp() {
      this.$store.dispatch("copyApp", {
        appID: this.copiedApp.appID,
        appName: this.copyAppName,
        app: this.copiedApp,
        clientHash: this.clientHash,
      });
      this.toggleModal();
    },
    renameApp(payload) {
      this.$store.dispatch("renameApp", payload);
    },
    toggleAddAppModal() {
      this.addAppStatus = !this.addAppStatus;
    },
    async validateForm() {
      const valid = await this.$refs["modal-form"].validate();

      if (valid) {
        switch (this.modalType) {
          case "rename": {
            this.renameApp();
            break;
          }
          case "copy": {
            this.copyApp();
            break;
          }
          default: {
            this.createNewApp();
            break;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.build {

  &__loader {
    background-color: white;
    padding: 16px;
  }

  margin: 0 32px 0 32px;

  @media (max-width: 1024px) {
    margin: 0 20px 0 20px;
  }

  &__filter {
    width: 205px;
    @media (max-width: 1024px) {
      width: 100%;
    }
  }

  &__header-panel {
    margin-bottom: 40px;
    @media (max-width: 1024px) {
      margin-bottom: 8px;
    }
  }

  &__app-select-wrapper {
    display: flex;
    align-items: center;
  }

  &__app-select {
    min-width: 160px;

    &--label {
      font-size: var(--font-paragraph-2-size);
      font-weight: var(--font-extra-bold);
      line-height: 1.33;
      // color: var(--apio-shade-2);
      margin-right: 8px;
    }
  }

  &__modal-actions {
    display: flex;
  }
}
</style>
